import { View } from 'react-native'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components/native'
import I18n from 'APP/I18n'
import { Header } from './header'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import Footer from './footer'
import { useAddDependent, AddDependentProvider } from 'APP/Components/AddDependent/hooks/provider'
import AddDependent from 'APP/Components/AddDependent'
import { useAddDependentApi } from 'APP/Components/AddDependent/hooks/useAddDependentApi'
import { useSelector } from 'APP/Hooks/redux'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'

const StyledView = styled(View)`
  flex: 1;
`

const SectionContainer = styled(ResponsiveView)`
  padding: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

const StyledResponsiveScrollView = styled(ResponsiveScrollView)`
  flex: 1;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 0.5}px;
`

const FooterContainer = styled(ResponsiveView)`
  padding-bottom: 0px;
`

type AddFamilyMemberScreenContentProps = {
  onClose?: () => void
}

const AddFamilyMemberScreenContent = ({ onClose }: AddFamilyMemberScreenContentProps) => {
  const { addDependentForm } = useAddDependent()
  const { currentStep } = addDependentForm
  const { adultInviteRunning, onSubmit } = useAddDependentApi()

  const isDisabled = adultInviteRunning
  return (
    <StyledView>
      <StyledResponsiveScrollView
        alwaysBounceVertical={false}
        wrapIntoView
        contentContainerInnerStyle={{}}
        contentContainerStyle={{ paddingBottom: 20 }}
      >
        <Header
          title={I18n.t('FamilyScreen.AddFamilyMemberScreen.title', { context: currentStep })}
          description={I18n.t('FamilyScreen.AddFamilyMemberScreen.description', {
            context: currentStep,
          })}
          onClose={onClose}
        />
        <SectionContainer>
          <AddDependent />
        </SectionContainer>
      </StyledResponsiveScrollView>
      <FooterContainer>
        <Footer currentStep={currentStep} onSubmit={onSubmit} isDisabled={isDisabled} />
      </FooterContainer>
    </StyledView>
  )
}

// This component is rendered in navigation and also as a regular component,
// hence the union of interfaces. For simplicity, the second interface is a subset of the first.
export type AddFamilyMemberScreenProps =
  | NativeStackScreenProps<ReactNavigation.RootParamList, 'addFamilyMember'>
  | { route: { params: ReactNavigation.RootParamList['addFamilyMember'] } }

const AddFamilyMemberScreen = ({ route: { params } }: AddFamilyMemberScreenProps) => {
  const { onAdultAdded, onChildAdded, onClose } = params
  const latestFamilyMembers = useSelector((state) => state.family.members)
  const familyMembers = useRef(latestFamilyMembers)

  useEffect(() => {
    // check if an adult/child is added
    const latestFamilyMembersKey = Object.keys(latestFamilyMembers)
    const currentFamilyMembersKey = Object.keys(familyMembers.current)

    if (latestFamilyMembersKey.length === currentFamilyMembersKey.length + 1) {
      const newFamilyMember = latestFamilyMembersKey
        .filter((key) => !familyMembers.current[key])
        .map((key) => latestFamilyMembers[key])

      if (newFamilyMember.length === 1) {
        if (newFamilyMember[0].type === 'guardian') {
          onAdultAdded?.(newFamilyMember[0].id)
        } else if (newFamilyMember[0].type === 'child') {
          onChildAdded?.(
            newFamilyMember[0].id,
            newFamilyMember[0].givenName,
            newFamilyMember[0].familyName,
            newFamilyMember[0].birthdate
          )
        }
      }
      familyMembers.current = latestFamilyMembers
    }
  }, [latestFamilyMembers, familyMembers, onAdultAdded, onChildAdded])

  return (
    <AddDependentProvider>
      <AddFamilyMemberScreenContent onClose={onClose} />
    </AddDependentProvider>
  )
}

export default AddFamilyMemberScreen
